import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useRef} from 'react';

import {KeyboardActionsEnum} from '../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {draftsCounterKey} from '../../../../helpers/counterHelpers';
import {buildTeammatePath} from '../../../../helpers/modelHelpers';
import {
  VisualWorkspaceFolderNestedItem,
  VisualWorkspaceFolderRootLabel,
  VisualWorkspaceTag,
} from '../../../../models/api/visualWorkspaceModel';
import {WorkspaceFolderRootLabelsEnum} from '../../../../models/api/workspaceModel';
import {TeammatesMessageFoldersEnum} from '../../../../models/url/inboxesUrlModel';
import {WorkspaceDisplaySectionTrackingTypesEnum} from '../../../../services/tracking';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {TeammateWorkspaceInfoBubble} from '../../../infoBubbles/teammateWorkspaceInfoBubble';
import {Workspace} from '../workspace';
import {WorkspaceGenericItemContainer} from '../workspaceGenericItemContainer';
import {WorkspaceHeader} from '../workspaceHeader';
import {TeammateWorkspaceCustomViewsContainer} from './teammateWorkspaceCustomViewsContainer';
import {TeammateWorkspaceInboxTagContainer} from './teammateWorkspaceInboxTagContainer';
import {TeammateWorkspaceStarItemContainer} from './teammateWorkspaceStarItemContainer';

/*
 * Props.
 */

export interface TeammateWorkspaceProps {
  panelId: InboxesPanelId;
  /** Our user's id. */
  userId: number;
  /** Our user's namespace. */
  userNamespace: string;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpandClick: Handler;
  /** Navigate to the user's Inbox. */
  onNavigateToInboxClick: Handler;
  /** Are drafts and sent hidden. */
  shouldHideDraftsAndSent: boolean;
  /** Whether there are any messages in the outbox. */
  isOutboxEmpty: boolean;
  /** Our user's starred tag id. */
  userStarredTagId: number;
  visualWorkspaceRootFolders: ReadonlyArray<VisualWorkspaceFolderRootLabel>;
  visualWorkspaceNestedFolders: ReadonlyArray<VisualWorkspaceFolderNestedItem>;
  // Represents both private and team tags that appear in this workspace.
  visualWorkspaceTags: ReadonlyArray<VisualWorkspaceTag>;
  shouldAlphabetizeWorkspace: boolean;
}

/*
 * Component.
 */

/** Display the workspace for our user. */
export const TeammateWorkspace: FC<TeammateWorkspaceProps> = (props) => {
  const {isExpanded, onNavigateToInboxClick} = props;
  const keyHandlers = {
    [KeyboardActionsEnum.GO_TO_INBOX]: onNavigateToInboxClick,
  };

  const onToggleExpandClick = () => {
    props.onToggleExpandClick();
  };

  const workspaceRef = useRef<HTMLDivElement>(null);

  return (
    <KeyboardShortcuts handlers={keyHandlers}>
      <Workspace ref={workspaceRef} isExpanded={isExpanded}>
        {/* Header. */}
        <WorkspaceHeader
          interactionId="teammate"
          isExpanded={isExpanded}
          onToggleExpandClick={onToggleExpandClick}
        >
          <Trans id="workspace-teammate-title">Me</Trans>
        </WorkspaceHeader>

        {/* Workspace content. */}
        {maybeRenderExpandedContent(props)}
      </Workspace>
      <TeammateWorkspaceInfoBubble targetRef={workspaceRef} />
    </KeyboardShortcuts>
  );
};

function maybeRenderExpandedContent(props: TeammateWorkspaceProps) {
  const {
    panelId,
    isExpanded,
    userId,
    shouldAlphabetizeWorkspace,
    visualWorkspaceNestedFolders,
    visualWorkspaceTags,
  } = props;
  if (!isExpanded) {
    return null;
  }

  const shouldShowOutbox = !props.isOutboxEmpty;

  return (
    <>
      {/* Inbox. */}
      <TeammateWorkspaceInboxTagContainer
        panelId={panelId}
        visualWorkspaceNestedFolders={visualWorkspaceNestedFolders}
      />

      {/* Root-level label folders. */}
      {renderVisualWorkspaceRootFolders(props)}

      {shouldShowOutbox ? (
        <>
          {/* Outbox. */}
          <WorkspaceGenericItemContainer
            shouldAlphabetizeWorkspace={false}
            canBeHidden={false}
            shouldHideMoreMenu={false}
            panelId={panelId}
            iconName="outboxSmall"
            selectedIconName="outboxFilledSmall"
            to={buildTeammatePath(userId, TeammatesMessageFoldersEnum.OUTBOX)}
            workspaceDragItem={undefined}
          >
            <Trans id="workspace-outbox">Outbox</Trans>
          </WorkspaceGenericItemContainer>
        </>
      ) : null}

      {/* Custom views. */}
      <TeammateWorkspaceCustomViewsContainer
        panelId={panelId}
        visualWorkspaceTags={visualWorkspaceTags}
        shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
      />
    </>
  );
}

function renderVisualWorkspaceRootFolders(props: TeammateWorkspaceProps) {
  const {
    panelId,
    userId,
    shouldHideDraftsAndSent,
    userStarredTagId,
    userNamespace,
    visualWorkspaceRootFolders,
  } = props;

  return _(visualWorkspaceRootFolders)
    .filter(({isSelected}) => isSelected)
    .map((folder) => {
      switch (folder.id) {
        case WorkspaceFolderRootLabelsEnum.STARRED:
          return (
            <TeammateWorkspaceStarItemContainer
              key={folder.id}
              panelId={panelId}
              starredTagId={userStarredTagId}
              namespace={userNamespace}
              canBeHidden
              shouldHideMoreMenu={false}
            />
          );

        case WorkspaceFolderRootLabelsEnum.DRAFTS:
          return !shouldHideDraftsAndSent ? (
            <WorkspaceGenericItemContainer
              key={folder.id}
              shouldAlphabetizeWorkspace={false}
              canBeHidden
              shouldHideMoreMenu={false}
              trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.DRAFTS}
              panelId={panelId}
              iconName="draftSmall"
              selectedIconName="draftFilledSmall"
              to={buildTeammatePath(userId, TeammatesMessageFoldersEnum.DRAFTS)}
              counterKey={draftsCounterKey}
              label={WorkspaceFolderRootLabelsEnum.DRAFTS}
              workspaceDragItem={undefined}
              interactionId="workspace-user-drafts"
            >
              <Trans id="workspace-drafts">Drafts</Trans>
            </WorkspaceGenericItemContainer>
          ) : null;

        case WorkspaceFolderRootLabelsEnum.SENT:
          return !shouldHideDraftsAndSent ? (
            <WorkspaceGenericItemContainer
              key={folder.id}
              shouldAlphabetizeWorkspace={false}
              canBeHidden
              shouldHideMoreMenu={false}
              trackingSection={WorkspaceDisplaySectionTrackingTypesEnum.SENT}
              panelId={panelId}
              iconName="sendSmall"
              selectedIconName="sendFilledSmall"
              to={buildTeammatePath(userId, TeammatesMessageFoldersEnum.SENT)}
              label={WorkspaceFolderRootLabelsEnum.SENT}
              workspaceDragItem={undefined}
              interactionId="workspace-user-sent"
            >
              <Trans id="workspace-sent">Sent</Trans>
            </WorkspaceGenericItemContainer>
          ) : null;

        default:
          return null;
      }
    })
    .compact()
    .value();
}
